// Bilingual plain-language recurrence summary. Hand-rolled over the builder's
// finite grammar (frequency word x interval x BYDAY/BYMONTHDAY list x end
// clause) — rrule.js `toText()` is EN-only and covers a grammar superset, so it
// is deliberately not used. Sentence templates live under the
// `schedules.wizard.summary.*` keys of the `test-cadence` namespace (EN + ES);
// weekday/month-day names come from `Intl`.

import { type TFunction } from "i18next";

import {
  WEEKDAY_ORDER,
  parseLocalDate,
  type RecurrenceBuilderState,
  type Weekday,
} from "./recurrence-rrule";

export interface RecurrenceSummary {
  /** "Every 3 months on day 1, starting Jul 1, 2026 — no end date." */
  sentence: string;
  /**
   * "4 inspections per facility per year" — approximate annual cadence,
   * rounded to the nearest whole number; null when the rule recurs less than
   * once a year (e.g. every 2 years), where a per-year line would mislead.
   */
  cadenceLine: string | null;
}

const SUMMARY = "schedules.wizard.summary";

// The week of Monday 2024-01-01 anchors Intl weekday names for chip codes.
const WEEKDAY_REFERENCE_DATE: Record<Weekday, string> = {
  MO: "2024-01-01",
  TU: "2024-01-02",
  WE: "2024-01-03",
  TH: "2024-01-04",
  FR: "2024-01-05",
  SA: "2024-01-06",
  SU: "2024-01-07",
};

function weekdayName(code: Weekday, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(
    parseLocalDate(WEEKDAY_REFERENCE_DATE[code]),
  );
}

function joinList(items: string[], locale: string): string {
  return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(items);
}

function formatMediumDate(isoDate: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(parseLocalDate(isoDate));
}

function formatMonthDay(isoDate: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" }).format(
    parseLocalDate(isoDate),
  );
}

function monthDaysPhrase(t: TFunction, locale: string, monthDays: ReadonlySet<number>): string {
  const positives = [...monthDays].filter((day) => day > 0).sort((a, b) => a - b);
  const hasLastDay = monthDays.has(-1);
  if (positives.length === 0) {
    return hasLastDay ? t(`${SUMMARY}.lastDayOfMonth`) : "";
  }
  if (positives.length === 1 && !hasLastDay) {
    return t(`${SUMMARY}.dayOfMonth`, { day: positives[0] });
  }
  const items = positives.map(String);
  if (hasLastDay) {
    items.push(t(`${SUMMARY}.lastDayOfMonth`));
  }
  return t(`${SUMMARY}.daysOfMonth`, { days: joinList(items, locale) });
}

function rulePhrase(
  t: TFunction,
  locale: string,
  state: RecurrenceBuilderState,
  startDate: string,
): string {
  switch (state.freq) {
    case "daily":
      return t(`${SUMMARY}.daily`, { count: state.interval });
    case "weekly": {
      const days = WEEKDAY_ORDER.filter((day) => state.weekdays.has(day)).map((day) =>
        weekdayName(day, locale),
      );
      return t(`${SUMMARY}.weekly`, { count: state.interval, days: joinList(days, locale) });
    }
    case "monthly":
      return t(`${SUMMARY}.monthly`, {
        count: state.interval,
        days: monthDaysPhrase(t, locale, state.monthDays),
      });
    case "yearly":
      return t(`${SUMMARY}.yearly`, {
        count: state.interval,
        date: formatMonthDay(startDate, locale),
      });
  }
}

function endPhrase(
  t: TFunction,
  locale: string,
  state: RecurrenceBuilderState,
  endDate: string | null,
): string {
  if (state.ends.type === "after") {
    return t(`${SUMMARY}.endAfter`, { count: state.ends.count });
  }
  const date = state.ends.type === "on" ? state.ends.date : endDate;
  if (date) {
    return t(`${SUMMARY}.endOn`, { date: formatMediumDate(date, locale) });
  }
  return t(`${SUMMARY}.endNever`);
}

// Empty chip sets behave like a single chip for the rate: a weekly rule with
// no BYDAY generates once per week anchored to the start date.
function occurrencesPerYear(state: RecurrenceBuilderState): number {
  switch (state.freq) {
    case "daily":
      return 365 / state.interval;
    case "weekly":
      return (52 * Math.max(state.weekdays.size, 1)) / state.interval;
    case "monthly":
      return (12 * Math.max(state.monthDays.size, 1)) / state.interval;
    case "yearly":
      return 1 / state.interval;
  }
}

/**
 * Build the always-visible plain-language summary for a recurrence. The
 * operator-facing trust line: the sentence states what the rule does and the
 * cadence line states how often per year. `startDate` must be a `YYYY-MM-DD`
 * date (the builder gates rendering on it); `endDate` is the wizard-level end
 * date and is used for the end clause when `state.ends` does not carry one.
 */
export function summarizeRecurrence(
  t: TFunction,
  locale: string,
  state: RecurrenceBuilderState,
  startDate: string,
  endDate: string | null,
): RecurrenceSummary {
  const sentence = t(`${SUMMARY}.sentence`, {
    rule: rulePhrase(t, locale, state, startDate),
    startDate: formatMediumDate(startDate, locale),
    end: endPhrase(t, locale, state, endDate),
  });
  const perYear = occurrencesPerYear(state);
  const cadenceLine =
    perYear >= 1 ? t(`${SUMMARY}.cadencePerYear`, { count: Math.round(perYear) }) : null;
  return { sentence, cadenceLine };
}
